/**
 * <hy-product-gallery>: Verbesserung für die Medien-Galerie aus
 * snippets/hy-product-gallery.liquid. Reine Erweiterung — ohne dieses
 * Modul bleibt die Galerie über native Scroll-Snap-Positionen und die
 * Thumbnail-Anker (echte In-Page-Links) vollständig bedienbar. Mit diesem
 * Modul: Pfeile/Zähler werden sichtbar ([data-hy-gallery-ready] setzt
 * ihr display frei, siehe sections/hy-main-product.liquid), Thumbnail-
 * aria-current folgt der Bildlaufposition, Pfeiltasten navigieren, und
 * ein hy:variant:changed-Event scrollt zum passenden Medium — ohne neuen
 * Bildrequest, da dasselbe <img>-Element im DOM bleibt und nur
 * scrollIntoView aufgerufen wird. Die Bildansicht (Zoom) ist seit AP-272
 * ein eigenes Element (assets/hy-product-lightbox.js); nach dem Schließen
 * scrollt hy:lightbox:closed die Galerie auf das zuletzt gezeigte Bild.
 */
class HyProductGallery extends HTMLElement {
  #controller = null;
  #observer = null;
  #slides = [];
  #currentIndex = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#slides = Array.from(this.querySelectorAll('.hy-product-gallery__slide'));

    this.querySelector('.hy-product-gallery__arrow--prev')
      ?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('.hy-product-gallery__arrow--next')
      ?.addEventListener('click', () => this.#step(1), { signal });

    this.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('hy:variant:changed', this.#handleMediaSelected, { signal });
    document.addEventListener('hy:lightbox:closed', this.#handleMediaSelected, { signal });

    const track = this.querySelector('.hy-product-gallery__track');
    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: track,
      threshold: 0.6,
    });
    this.#slides.forEach((slide) => this.#observer.observe(slide));

    // Erst jetzt, da Pfeile/Zähler ohne eine per JS verfolgte
    // Bildlaufposition nichts Sinnvolles täten.
    this.setAttribute('data-hy-gallery-ready', '');
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.#slides.indexOf(entry.target);
      if (index !== -1) this.#setCurrent(index);
    }
  };

  #setCurrent(index) {
    this.#currentIndex = index;

    const counter = this.querySelector('.hy-product-gallery__counter-current');
    if (counter) counter.textContent = String(index + 1);

    this.querySelectorAll('.hy-product-gallery__thumbnail').forEach((thumbnail, i) => {
      if (i === index) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });

    // Position dots of the tile mode (phones); absent otherwise.
    this.querySelectorAll('.hy-product-gallery__dot').forEach((dot, i) => {
      dot.toggleAttribute('data-current', i === index);
    });
  }

  #step(direction) {
    const nextIndex = (this.#currentIndex + direction + this.#slides.length) % this.#slides.length;
    this.#scrollToIndex(nextIndex);
  }

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };

  // hy:variant:changed and hy:lightbox:closed carry the same detail shape.
  #handleMediaSelected = (event) => {
    const { sectionId, mediaId } = event.detail || {};
    if (!mediaId || sectionId !== this.dataset.sectionId) return;

    const targetId = `HyMedia-${this.dataset.productId}-${mediaId}`;
    const index = this.#slides.findIndex((slide) => slide.id === targetId);
    if (index !== -1) this.#scrollToIndex(index);
  };

  #scrollToIndex(index) {
    const slide = this.#slides[index];
    if (!slide) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slide.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }
}

customElements.define('hy-product-gallery', HyProductGallery);
